import React, { useEffect, useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ToastContext } from '../App';
import ChangePasswordModal from './ChangePasswordModal';
import '../styles/navbar.css';

const Navbar: React.FC = () => {
  const { user, logout, pendingStudentsCount, pendingPaymentsCount } = useAuth();
  const navigate = useNavigate();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const { showToast } = useContext(ToastContext);

  // Calcular total de notificações
  const totalNotifications = pendingStudentsCount + pendingPaymentsCount;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };

  const handleNavLinkClick = () => {
    const navbarCollapse = document.getElementById('navbarNav');
    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
      navbarCollapse.classList.remove('show');
    }
  };

  // Se não há utilizador, mostrar apenas navbar básica com login/registo
  if (!user) {
    return (
      <nav className="navbar navbar-expand-lg navbar-dark shadow-md" style={{ backgroundColor: '#a2449c', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/login">
            <img 
              src="/logo_white.png" 
              alt="ACE Agility Club" 
              height="28" 
              className="me-2"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          {/* Removido menu Entrar e Registar */}
        </div>
      </nav>
    );
  }

  // Se há utilizador, mostrar navbar completa baseada no role
  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark shadow-md" style={{ backgroundColor: '#a2449c', paddingTop: '0.5rem', paddingBottom: '0.5rem' }}>
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <img 
              src="/logo_white.png" 
              alt="ACE Agility Club" 
              height="28" 
              className="me-2"
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          </Link>
          
          {/* Help icon for mobile - outside collapsible menu */}
          <div className="d-lg-none ms-auto">
            <ul className="navbar-nav">
              {user.role === 'student' && (
                <li className="nav-item">
                  <Link className="nav-link help-icon" to="/help" style={{ color: '#deae26' }}>
                    <i className="fas fa-question-circle"></i>
                  </Link>
                </li>
              )}
            </ul>
          </div>
          
          <button 
            className="navbar-toggler position-relative" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
            {/* Badge de notificações apenas para trainers em modo responsivo */}
            {user.role === 'trainer' && totalNotifications > 0 && (
              <span 
                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                style={{ 
                  fontSize: '0.6rem', 
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1000
                }}
              >
                {totalNotifications}
              </span>
            )}
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav me-auto">
              {/* Calendário - disponível para todos */}
              <li className="nav-item">
                <Link className="nav-link d-flex align-items-center" to="/calendar" onClick={handleNavLinkClick}>
                  <i className="fas fa-calendar me-1"></i>
                  <span>Calendário</span>
                </Link>
              </li>
              
              {/* Menus específicos para trainers */}
              {user.role === 'trainer' && (
                <>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/availability" onClick={handleNavLinkClick}>
                      <i className="fas fa-clock me-1"></i>
                      <span>Disponibilidades</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/students" onClick={handleNavLinkClick}>
                      <i className="fas fa-users me-1"></i>
                      <span>Alunos</span>
                      {pendingStudentsCount > 0 && (
                        <span className="badge bg-warning text-dark ms-1">
                          {pendingStudentsCount}
                        </span>
                      )}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/animals" onClick={handleNavLinkClick}>
                      <i className="fas fa-paw me-1"></i>
                      <span>Animais</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/payments" onClick={handleNavLinkClick}>
                      <i className="fas fa-credit-card me-1"></i>
                      <span>Pagamentos</span>
                      {pendingPaymentsCount > 0 && (
                        <span className="badge bg-warning text-dark ms-1">
                          {pendingPaymentsCount}
                        </span>
                      )}
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/settings" onClick={handleNavLinkClick}>
                      <i className="fas fa-cog me-1"></i>
                      <span>Configurações</span>
                    </Link>
                  </li>
                </>
              )}

              {/* Menus específicos para estudantes e externos */}
              {(user.role === 'student' || user.role === 'external') && (
                <>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/animals" onClick={handleNavLinkClick}>
                      <i className="fas fa-paw me-1"></i>
                      <span>Meus Animais</span>
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link className="nav-link d-flex align-items-center" to="/account" onClick={handleNavLinkClick}>
                      <i className="fas fa-wallet me-1"></i>
                      <span>Minha Conta</span>
                    </Link>
                  </li>
                </>
              )}

                            {/* Menus para todos os utilizadores autenticados (exceto guest) */}
              {user.role !== 'guest' && (
                <li className="nav-item">
                  <Link className="nav-link d-flex align-items-center" to="/statistics" onClick={handleNavLinkClick}>
                    <i className="fas fa-chart-bar me-1"></i>
                    <span>Estatísticas</span>
                  </Link>
                </li>
              )}

              {/* FlowAgility - disponível para todos os utilizadores */}
              <li className="nav-item">
                <button 
                  className="nav-link d-flex align-items-center" 
                  onClick={() => {
                    // Fechar menu mobile primeiro
                    const navbarCollapse = document.getElementById('navbarNav');
                    if (navbarCollapse && navbarCollapse.classList.contains('show')) {
                      navbarCollapse.classList.remove('show');
                    }
                    
                    // Mostrar mensagem de redirecionamento
                    showToast('A redirecionar para FlowAgility... Pode demorar um pouco, dependendo da velocidade de resposta da FlowAgility.', 'info');
                    
                    // Redirecionar após um pequeno delay para o toast aparecer
                    setTimeout(() => {
                      const currentUrl = window.location.href;
                      sessionStorage.setItem('aceBookingReturnUrl', currentUrl);
                      window.location.href = 'https://flowagility.com';
                    }, 1500);
                  }}
                  style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}
                >
                  <i className="fas fa-external-link-alt me-1"></i>
                  <span>FlowAgility</span>
                </button>
              </li>

              {/* CPC Agility - dropdown com submenu */}
              <li className="nav-item dropdown">
                <button
                  className="nav-link dropdown-toggle d-flex align-items-center"
                  id="cpcAgilityDropdown"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ background: 'none', border: 'none', color: 'inherit' }}
                >
                  <i className="fas fa-dog me-1"></i>
                  <span>CPC Agility</span>
                </button>
                <ul className="dropdown-menu animate-fade-in" aria-labelledby="cpcAgilityDropdown">
                  <li>
                    <Link className="dropdown-item d-flex align-items-center" to="/calendar-provas" onClick={handleNavLinkClick}>
                      <i className="fas fa-calendar-alt me-2"></i>
                      <span>Calendário de Provas</span>
                    </Link>
                  </li>
                  <li>
                    <Link className="dropdown-item d-flex align-items-center" to="/ranking" onClick={handleNavLinkClick}>
                      <i className="fas fa-trophy me-2"></i>
                      <span>Ranking</span>
                    </Link>
                  </li>
                </ul>
              </li>

              {/* Tempo - disponível para todos os utilizadores autenticados */}
              <li className="nav-item">
                <Link className="nav-link d-flex align-items-center" to="/weather" onClick={handleNavLinkClick}>
                  <i className="fas fa-cloud-sun me-1"></i>
                  <span>Tempo</span>
                </Link>
              </li>
            </ul>
            
            <ul className="navbar-nav">
              {/* Help icon for desktop - left side of user dropdown */}
              <li className="nav-item d-none d-lg-block">
                {user.role === 'student' && (
                  <Link className="nav-link help-icon" to="/help" style={{ color: '#deae26' }}>
                    <i className="fas fa-question-circle"></i>
                  </Link>
                )}
              </li>
              
              <li className="nav-item dropdown">
                <button 
                  className="nav-link dropdown-toggle d-flex align-items-center" 
                  id="userDropdown" 
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  style={{ background: 'none', border: 'none', color: 'inherit' }}
                >
                  <i className="fas fa-user me-1"></i>
                  <span>{user.name}</span>
                </button>
                <ul className="dropdown-menu dropdown-menu-end animate-fade-in">
                  <li>
                    <button className="dropdown-item d-flex align-items-center" onClick={handleChangePassword} style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}>
                      <i className="fas fa-key me-2"></i>
                      <span>Alterar Password</span>
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item d-flex align-items-center" onClick={handleLogout} style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left' }}>
                      <i className="fas fa-sign-out-alt me-2"></i>
                      <span>Sair</span>
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </>
  );
};

export default Navbar; 